/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数。 親コンポーネント再 render 時に再生成されるが、
 * MasterDataTable は cell の戻り値を <TableCell> の children として表示するだけで
 * 独立した component subtree を持たないため、 lint 警告の前提 (= subtree 破棄)
 * に該当しない。
 */
import type { MouseEvent } from 'react'

import { timestampDate } from '@bufbuild/protobuf/wkt'
import { WorkflowCaseViewerAction } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useNavigate } from 'react-router'

import {
  Box,
  HStack,
  LightModeSemanticColors,
  Link,
  SecondaryButton,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'

import { Badge } from '~/components/common/Badge'
import {
  DeleteIconButton,
  DetailIconButton,
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { sortableColumn } from '~/components/common/SortableColumnHeader'
import { WorkflowCaseStatusBadge } from '~/components/common/WorkflowCaseStatusBadge'
import { WorkflowCaseTagList } from '~/components/common/WorkflowCaseTagList'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { openOrNavigate } from '~/libs/openOrNavigate'
import {
  formatDateDisplay,
  formatDateTimeFull,
  fromProtoDate,
} from '~/models/dateTime'
import { summarizeMemberNames } from '~/models/memberListDisplay'
import { localizeBuiltinWorkflowText } from '~/models/workflowBuiltinText'
import { areaLabelOrDash } from '~/models/workflowCase'
import { isWorkflowCaseDeletable } from '~/models/workflowCaseDeletable'
import { withActiveSubStepFocus } from '~/models/workflowCaseFocusSignal'
import { isWorkflowCaseReadonly } from '~/models/workflowCaseReadonly'
import type {
  SortBy,
  SortOrder,
} from '~/pages/WorkflowCases/models/sortWorkflowCases'
import {
  WORKFLOW_CASE_BG_ABSENT_LABEL,
  WORKFLOW_CASE_NO_VALUE_LABEL,
  WORKFLOW_CASE_UNRESOLVED_MEMBER_LABEL,
  workflowCaseBgLicenseBadgeKey,
  workflowCaseSectionLabelKey,
  workflowCaseViewerActionLabelKey,
} from '~/pages/WorkflowCases/models/workflowCase'
import type { WorkflowCaseRow } from '~/pages/WorkflowCases/models/workflowCase'
import type { WorkflowTargetCodeKind } from '~/pages/WorkflowCases/models/workflowTargetCodeKind'
import { routePaths } from '~/routes/routePaths'

function labelOrDash(
  key: string | undefined,
  t: (key: string) => string,
): string {
  return key === undefined ? '-' : t(key)
}

/** 対象なし (`WORKFLOW_CASE_NO_VALUE_LABEL` = 「—」) セル向けの labelOrDash 相当。 */
function labelOrNoValue(
  key: string | undefined,
  t: (key: string) => string,
): string {
  return key === undefined ? WORKFLOW_CASE_NO_VALUE_LABEL : t(key)
}

/**
 * 進捗セル (spec AC-WFW-001: 完了サブステップ数 / 全サブステップ数 のプログレスバー)。
 * `WorkflowCaseDetail/components/WizardStepper.tsx` の raw div プログレスバーと同じ表現を
 * テーブルセル用に小型化して踏襲する (= ecloud に table セル向けの progress primitive が
 * 無いため、 既存の raw div パターンを再利用)。
 */
const ProgressCell = ({
  done,
  total,
  activeStepName,
}: {
  done: number
  total: number
  /** 現在アクティブな大ステップ名 (上段)。 全完了・未設定は undefined。 */
  activeStepName: string | undefined
}): React.ReactElement => {
  const percent = total > 0 ? Math.round((done / total) * 100) : 0
  // design `cases.html` の `allDone = (seed.done >= seed.total)` と同じ判定。
  // total === 0 は幅 0% で不可視だが、意図を明示するため明示的に除外する。
  const isDone = total > 0 && done >= total
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        minWidth: '72px',
      }}
    >
      {/* 上段 = 現在アクティブな大ステップ名 (8080 `demand/cases.html` の
          `.progress-next`)。 「2/13」 だけでは その 2 が何のステップか読み手に伝わらず、
          8080 の th title も「現在着手中のステップ (タスクボールの位置)」 としている
          (2026-08-26 デザイナー指摘)。 全完了時は出さない (= 下段の 13/13 で足りる)。 */}
      {activeStepName !== undefined && (
        <Text textStyle="tableCell" color="Text.highEmphasis">
          {activeStepName}
        </Text>
      )}
      {/* design `.progress-label` (cases.html): xs / low-emphasis / tabular-nums。
          等幅数字にしないと「3/8」と「13/13」で桁位置が揺れて縦に並ばない。 */}
      <Text
        color="Text.lowEmphasis"
        fontSize="xs"
        fontVariantNumeric="tabular-nums"
      >{`${done}/${total}`}</Text>
      {/* design `.progress-bar`: 56px 固定 / 5px / 地色 + 中立枠 / 全角丸。
          固定幅なのは「バーが数値ラベルの桁数に影響されない = 列幅が行ごとに
          ジャンプしない」ため (design 側の同旨コメント)。 */}
      <Box
        flexShrink={0}
        overflow="hidden"
        width="56px"
        height="5px"
        background="Surface.secondary"
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="full"
      >
        <div
          style={{
            height: '100%',
            width: `${percent}%`,
            // design `.progress-fill` は無彩色 (未完了 = low / 完了 = mid)。
            // 進捗は「どれだけ進んだか」を示すだけで良否を表さないので、
            // 緑 (positive) は意味を足しすぎる。
            background: isDone
              ? LightModeSemanticColors.Text.midEmphasis.value
              : LightModeSemanticColors.Text.lowEmphasis.value,
          }}
        />
      </Box>
    </div>
  )
}

/**
 * 対応者セル (AC-WFW-001「対応者」/ workflow-list-action-columns AC-03/AC-09)。
 *
 * 対応者は重複排除後も複数名になるのが常態なので、列ズレを避けるため先頭 1 名 +
 * 「他 N 名」に畳み、全員は tooltip で開示する (= 「自分が動くべきか」は隣の
 * 「あなたの対応」列が単独で答えるため、この列が全員を出し切る必要はない)。
 *
 * 表示名は member 一覧から解決し、解決できない ID は hyphen に落として生の
 * eauth member ID を画面に出さない (AC-09、既存の監査列と同じ扱い)。
 */
const ResponderCell = ({
  memberIds,
  resolveDisplayName,
}: {
  memberIds: string[]
  resolveDisplayName: (memberId: string | undefined) => string | undefined
}): React.ReactElement => {
  const { t } = useTranslation()
  const names = memberIds.map(
    (id) => resolveDisplayName(id) ?? WORKFLOW_CASE_UNRESOLVED_MEMBER_LABEL,
  )
  const { lead, othersCount, allNames } = summarizeMemberNames(names)
  if (lead === undefined) {
    return (
      <Text as="span">{t('page:workflowCases.column.responderNotSet')}</Text>
    )
  }
  if (othersCount === 0) {
    return <Text as="span">{lead}</Text>
  }
  return (
    <HStack gap={1}>
      <Text as="span">{lead}</Text>
      <Tooltip content={allNames.join(', ')} placement="top" showArrow>
        <Text as="span" color="Text.lowEmphasis" fontSize="xs">
          {t('page:workflowCases.column.responderOthers', {
            count: othersCount,
          })}
        </Text>
      </Tooltip>
    </HStack>
  )
}

type Props = {
  rows: WorkflowCaseRow[]
  /**
   * bgCode → 種別の突合 Map (spec AC-WFW-001 License バッジ表示用)。
   * 呼び出し側 (index.tsx) が `buildWorkflowTargetCodeKindByCode` で構築して渡す。
   */
  targetCodeKindByCode: ReadonlyMap<string, WorkflowTargetCodeKind>
  /** 「削除」ボタンクリック時のハンドラ (spec AC-003f: 削除確認ダイアログを開く、AC-01)。 */
  onDeleteClick: (row: WorkflowCaseRow) => void
  /** 現在のソート列。 */
  sortBy: SortBy
  /** 現在のソート順。 */
  sortOrder: SortOrder
  /** ソート列/順が変化した時のハンドラ。 */
  onSortByChange: (sortBy: SortBy, sortOrder: SortOrder) => void
}

export const CaseTable = ({
  rows,
  targetCodeKindByCode,
  onDeleteClick,
  sortBy,
  sortOrder,
  onSortByChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const resolveDisplayName = useMemberDisplayName()

  const orderOf = (column: SortBy): SortOrder | undefined =>
    sortBy === column ? sortOrder : undefined
  const nextOrderOf = (column: SortBy): SortOrder =>
    sortBy === column && sortOrder === 'desc' ? 'asc' : 'desc'
  const sortableColumnOf = (
    column: SortBy,
    label: string,
  ): ReturnType<typeof sortableColumn> =>
    sortableColumn({
      order: orderOf(column),
      label,
      nextOrder: nextOrderOf(column),
      onClick: () => {
        onSortByChange(column, nextOrderOf(column))
      },
    })

  const detailPath = (id: string): string =>
    routePaths.workflowCaseDetail.path.replace(':caseId', id)

  const handleDetailClick =
    (id: string) =>
    (e: MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation()
      openOrNavigate(navigate, detailPath(id), e)
    }

  // 要承認・要対応ボタン (D5 改訂, workflow-case-list-12col-viewer-action-4value
  // slice、2026-08-19 spec AC-WFW-001 §243 追従改訂): 両ボタンとも常に詳細画面
  // (`detailPath`) へ遷移し、アクティブサブステップへの focus signal を付与する。
  // 詳細画面がアクティブサブステップの状態 (承認待/進行中) に応じて承認セクション /
  // サブステップ行のどちらへフォーカスするかを自前で出し分ける
  // (`WorkflowCaseDetail` 側の focus 機構)。
  //
  // 旧 D5 (撤去済み): 要対応ボタンは業務 linked page (`resolveLinkedPage`) へ
  // 遷移していたが、spec §243 が両ボタンとも詳細画面遷移 + 該当サブステップ
  // focus を規定しており、業務 linked page には承認 UI が無く要対応ボタンが
  // 承認導線として不成立だったため撤去した (D6: linked page target 一式
  // (proto viewer_action_required_linked_page_kind/plan_id・domain
  // viewerActionRequiredSubStep・BFF resolve_linked_plan.go 中継) も dead code
  // 化するため合わせて撤去)。
  const handleActionRequiredClick =
    (row: WorkflowCaseRow) =>
    (e: MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation()
      openOrNavigate(navigate, withActiveSubStepFocus(detailPath(row.id)), e)
    }

  // 列構成は spec AC-WFW-001 の 12 列。
  // **列順は 8080 (`demand/cases.html` の thead) に合わせる**
  // (2026-08-26 / 2026-08-27 デザイナー判断、 spec は後追いで改訂予定):
  // 1: ステータス / 2: 進捗 / 3: あなたの対応 / 4: ワークフローID / 5: 対象日 /
  // 6: 計画断面 / 7: エリア / 8: BG コード・名称 / 9: タグ / 10: 対応者 /
  // 11: 最終更新 / 12: 操作 (右端 sticky、 renderRowActions 側で詳細+削除を描画)。
  //
  // 進捗 と あなたの対応 を前に出すのは「今どの状態で・どこまで進んだか・
  // **自分は何をすべきか**」 が左端 3 列で読み切れるようにするため。 8080 も
  // ステータス → 進捗 → あなたの対応 の順で、 その後に 承認フロー / タグ が続く。
  //
  // [8080 差異] 4 列目以降の並びは 8080 (承認フロー → タグ → 計画断面 → エリア →
  // BG → 対象日 → ワークフローID) と一致していない。 承認フロー列は実装に無く、
  // 残りの並び替えは本指摘の範囲外のため据え置く。
  //
  // [spec 差異] AC-WFW-001 は列順を左→右で固定し 進捗を 8 番目としている。
  // spec 改訂 issue は PR に紐付ける。
  //
  // [#7865 逸脱] ワークフローID の既定位置は「ステータスの直後 = 左から 2 列目」
  // だが、 **本テーブルは逸脱として 4 列目に据え置く**。 2 列目は PdM 判断で
  // 進捗が占めているため (8080 `demand/cases.html` の `enrichRows()` に
  // 「[PdM調整] 進捗列を ステータスの右 (2列目) へ移動」 とある)。 既定を当てると
  // その PdM 決定を上書きし、 8080 と React が再び食い違う。
  // 8080 側も同じ理由で逸脱として確定済み (ebalance-prototype#347)。
  // 既定そのもの (#7865) は変えていない。
  const columns: Array<MasterDataTableColumn<WorkflowCaseRow>> = [
    {
      key: 'status',
      // 状態のバッジは固定値の列なので中央揃え (UI 辞書 components/table.md「列揃えの規約」)
      align: 'center',
      nowrap: true,
      header: t('page:workflowCases.column.status'),
      cell: (row) => <WorkflowCaseStatusBadge status={row.status} />,
    },
    {
      key: 'progress',
      nowrap: true,
      ...sortableColumnOf('progress', t('page:workflowCases.column.progress')),
      cell: (row) => (
        <ProgressCell
          activeStepName={
            row.activeBigStepName === undefined
              ? undefined
              : localizeBuiltinWorkflowText(row.activeBigStepName, t)
          }
          done={row.progress.done}
          total={row.progress.total}
        />
      ),
    },
    {
      key: 'yourAction',
      align: 'center',
      nowrap: true,
      header: t('page:workflowCases.column.yourAction'),
      // あなたの対応 4 値 (workflow-case-list-12col-viewer-action-4value
      // slice D1/D3/D5): 要承認・要対応はボタン (件数なし)、待ち・— はテキスト。
      // design `cases.html` の要対応は `.btn-secondary` (= 塗りのある副ボタン)。
      // Tertiary は中立枠の弱い見た目で、一覧の中で「自分が動くべき行」が
      // 他の行と同じ強さに見えてしまう。
      cell: (row) => {
        switch (row.viewerAction) {
          case WorkflowCaseViewerAction.APPROVAL_REQUIRED:
            return (
              <SecondaryButton
                onClick={handleActionRequiredClick(row)}
                size="sm"
              >
                {t('page:workflowCases.viewerAction.approvalRequired')}
              </SecondaryButton>
            )
          case WorkflowCaseViewerAction.REQUIRED:
            return (
              <SecondaryButton
                onClick={handleActionRequiredClick(row)}
                size="sm"
              >
                {t('page:workflowCases.viewerAction.required')}
              </SecondaryButton>
            )
          default:
            return (
              <Text as="span">
                {labelOrNoValue(
                  workflowCaseViewerActionLabelKey(row.viewerAction),
                  t,
                )}
              </Text>
            )
        }
      },
    },
    {
      key: 'workflowId',
      align: 'left',
      nowrap: true,
      format: 'number',
      header: t('page:workflowCases.column.workflowId'),
      cell: (row) => (
        <Link asChild>
          <RouterLink
            onClick={(e) => {
              e.stopPropagation()
            }}
            to={detailPath(row.id)}
          >
            {row.workflowId}
          </RouterLink>
        </Link>
      ),
    },
    {
      key: 'targetDate',
      align: 'center',
      nowrap: true,
      format: 'number',
      ...sortableColumnOf(
        'targetDate',
        t('page:workflowCases.column.targetDate'),
      ),
      cell: (row) =>
        row.targetDate !== undefined
          ? formatDateDisplay(fromProtoDate(row.targetDate))
          : '-',
    },
    {
      key: 'section',
      align: 'center',
      nowrap: true,
      header: t('page:workflowCases.column.section'),
      cell: (row) => labelOrDash(workflowCaseSectionLabelKey(row.section), t),
    },
    {
      key: 'area',
      align: 'center',
      nowrap: true,
      header: t('page:workflowCases.column.area'),
      // issue #5219: BG 不要ケースは area_code が UNSPECIFIED で返る。
      // areaLabelOrDash が空欄でなく「—」に落とす (対象なしセルと同じ表記)。
      cell: (row) => areaLabelOrDash(row.areaCode),
    },
    {
      key: 'bg',
      nowrap: true,
      header: t('page:workflowCases.column.bgCode'),
      cell: (row) => {
        // AC-11: BG 軸を持たないケースは「—」1 つだけを出す (ライセンスバッジも
        // BG 名も存在しないため、空欄と区別できる表記に寄せる)。
        if (row.bgCode === undefined) {
          return (
            <Text as="span" textStyle="captionStrong">
              {WORKFLOW_CASE_BG_ABSENT_LABEL}
            </Text>
          )
        }
        const kind = targetCodeKindByCode.get(row.bgCode)
        const badgeKey =
          kind !== undefined ? workflowCaseBgLicenseBadgeKey(kind) : undefined
        return (
          // 外側の gap は 4px のまま。 8px にするのは BG コードと名称の間だけで、
          // コードとライセンスバッジ (需要 / 自託 / 発電) の間は従来どおり 4px
          // (デザイナー指摘は「BG コードとの間隔」 = コードと名称の境目)。
          <HStack gap={1}>
            {/* BG コード・名称は 8080 (`demand/cases.html` の `td.cell-bg`) が
                14px = md。 実装は コード captionStrong (12px) / 名称 xs (10px) と
                2 段階小さく、 同じ行の他セル (md) から沈んで読めなかった
                (2026-08-26 デザイナー指摘)。 両方 md に揃えてある。

                **太字にしない** (2026-09-02 デザイナー判断)。 コードを label
                (md/600) にしていたのは、 1 列併記時代に**同じセルの中で**コードと
                名称の主従を付けるためだった (当初は名称を low-emphasis にして色で
                付けていたが、 薄すぎて読みにくいというデザイナー指摘 2026-08-28 を
                受け、 主従を色から太さへ移した)。

                列を分けた本変更では、 **列見出し「BG コード」「BG 名称」 が区別を
                担う**ためセル内で主従を付ける理由が消える。 広域マスタ
                (`admin/occto.html` は元から「BGコード」「BG名称」 の 2 列) も値を
                太字にしておらず、 2 列構成の先例と揃う。

                需要想定一覧の BG 列 (`DemandForecastList/components/ForecastTable`)
                と同じ組み方に保つこと (spec PR #6748 で両画面の BG 列を揃える判断)。 */}
            <Text as="span" textStyle="tableCell" fontFamily="mono">
              {row.bgCode}
            </Text>
            {/* ライセンスバッジ (需要 / 自託 / 発電) は**コードの右**に置く。
                左に置くと、 バッジのある行だけコードが右へ寄って列の縦のラインが
                崩れる。 幅を確保する器を挟む案は、 今度はどの行にも左余白が
                できてしまい、 コードの左が間延びする
                (2026-09-02 デザイナー指摘)。 右に出せばコードは必ずセルの左端から
                始まり、 バッジの有無に関係なく揃う。 */}
            {badgeKey !== undefined && (
              <Badge status="neutral">{t(badgeKey)}</Badge>
            )}
          </HStack>
        )
      },
    },
    {
      // 2026-09-01 デザイナー指摘「dev 全体で『BG コード・名称』 は 2 つのカラムに
      // 分けて表示」。 ⚠️ spec AC-WFW-001 / AC-DFL-006 [P0] と衝突する変更で、
      // AC の再判断は issue #6952 で PdM に確認中。 **解決前にマージしないこと。**
      // ライセンスバッジ (需要 / 自託 / 発電) は BG コードの属性なので分割後も
      // **コード側の列に残す** (名称の属性ではない)。
      key: 'bgName',
      nowrap: true,
      header: t('page:workflowCases.column.bgName'),
      cell: (row) =>
        row.bgName === undefined || row.bgName === '' ? (
          <Text as="span" textStyle="captionStrong">
            {WORKFLOW_CASE_BG_ABSENT_LABEL}
          </Text>
        ) : (
          <Text as="span" textStyle="tableCell" color="Text.highEmphasis">
            {row.bgName}
          </Text>
        ),
    },
    {
      key: 'tags',
      header: t('page:workflowCases.column.tags'),
      cell: (row) => <WorkflowCaseTagList tags={row.tags} />,
    },
    {
      key: 'responder',
      nowrap: true,
      header: t('page:workflowCases.column.responder'),
      cell: (row) =>
        isWorkflowCaseReadonly(row) ? (
          <Text as="span">{WORKFLOW_CASE_NO_VALUE_LABEL}</Text>
        ) : (
          <ResponderCell
            memberIds={row.responderMemberIds}
            resolveDisplayName={resolveDisplayName}
          />
        ),
    },
    {
      key: 'updatedAt',
      nowrap: true,
      ...sortableColumnOf(
        'updatedAt',
        t('page:workflowCases.column.updatedAt'),
      ),
      cell: (row) =>
        row.updatedAt !== undefined
          ? formatDateTimeFull(timestampDate(row.updatedAt))
          : '-',
    },
  ]

  return (
    <MasterDataTable
      actionColWidth="88px"
      columns={columns}
      getRowKey={(row) => row.id}
      // 削除列は `renderRowActions` に寄せる (= 右端 sticky / zebra 追従 / hover 重ね塗り /
      // 影の出し分けを共通ロジック `stickyActionColumn.ts` から受け取る)。
      // 行クリック (onRowClick) で常時詳細画面へ到達可能 (AC-WFW-002)。
      // 「削除」は未着手・進行中のみ表示 (完了・削除済みは hidden、AC-01)。
      //
      // [design 差異] 8080 プロト (`design/demand/cases.html:127, 2396`
      // 「[mock同期 2026-07-13] 一覧の削除ボタン列は撤去」) は削除導線を詳細画面へ
      // 集約しているが、本画面はデザイナー判断 (2026-08-13) で一覧の削除導線を残す。
      // アイコン化のみデザイナー指摘 (2026-08-13「削除ボタンはゴミ箱アイコンに変更」)
      // を反映: 参照先だった取引契約管理の自前 `IconButton size="xs"` は boxSize 未指定
      // (DS 既定 24px) で行末アイコンの 20px にならないため、20px を明示済みの共通部品 `DeleteIconButton` に揃える
      // (8080 側の削除アイコン CSS 実測も 20px = `design/demand/cases.html:166` と一致)。
      //
      // 「詳細」も通常列ではなく操作列へ入れる (デザイナー指摘 2026-08-14
      // 「詳細ボタンの TH 削除 / ゴミ箱アイコンと合わせて固定カラム扱いに /
      // ゴミ箱の TD 左 padding は右に合わせる」)。 design も同じ形が正:
      // - `design/CLAUDE.md` L276「操作列（詳細ボタン等）は右揃え固定・TH ラベルなし」
      // - `design/demand/cases.html` L350 の `<th class="col-detail" aria-label="詳細"></th>`
      //   (= TH は中身が空で aria-label のみ)
      // - 同 L129-133 `.ecl-table th.col-detail, td.cell-detail { position: sticky;
      //   right: 0; padding-left: 8px; padding-right: 8px; }` (= 固定列・左右対称 padding)
      // - 同 `.cell-act` は「操作列に複数アクション (詳細 + 削除) を並べる」ラッパ
      // 操作列へ移すことで TH ラベル無し・右端固定・左右対称 padding (8px) が
      // `stickyActionColumn.ts` から自動で乗り、 指摘 3 点が同時に解消する。
      onRowClick={(row, e) => {
        openOrNavigate(
          navigate,
          routePaths.workflowCaseDetail.path.replace(':caseId', row.id),
          e,
        )
      }}
      // アイコン 32px × 2 + gap 8px + 左右 padding 16px = 88px。
      renderRowActions={(row) => (
        <>
          {/* [design 差異] 8080 (`design/demand/cases.html` の
              `<a class="btn btn-tertiary">詳細</a>`) と `design/CLAUDE.md`
              「詳細ボタンは compact 32px / font md 14px 固定」は詳細を
              **テキストボタン**としているが、デザイナー判断 (2026-08-18、#5749)
              で雛形一覧と同じアイコンボタンに揃える。 design / spec 側の追随は #5749。

              アイコンは **eye** (`DetailIconButton`)。 2026-08-18 時点では
              「この導線は実質『編集』の意味を持つ」 として編集アイコン
              (pen-to-square) を流用していたが、 2026-09-09 デザイナー指定
              「全画面対象、 アイコンボタンで、 編集ではなく詳細の場合は
              `FaEyeRegular` を適用して編集導線と区別できるように」 で差し替えた。
              **ラベルが『詳細』 なのに絵が『編集』 だと、 操作列を見ただけでは
              その行を開くと編集できるのかが読めない。** 名前とアイコンを揃える。 */}
          <DetailIconButton
            ariaLabel={t('page:workflowCases.column.detail')}
            onClick={handleDetailClick(row.id)}
          />
          {isWorkflowCaseDeletable(row.status) && (
            <DeleteIconButton
              onClick={() => {
                onDeleteClick(row)
              }}
            />
          )}
        </>
      )}
      rows={rows}
    />
  )
}
